import { isEthereumWallet } from "@dynamic-labs/ethereum";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { evaluate, toast } from "@kyan/ui";
import { useEffect, useState } from "react";
import { Address, formatUnits, parseUnits, WalletClient } from "viem";

import { UNISWAP_PERMIT2 } from "#/config/web3/contractAddresses";
import { useBridgeFeatureFlag } from "#/hooks/featureFlags/useBridgeFeatureFlag";
import { useDepositFeatureFlag } from "#/hooks/featureFlags/useDepositFeatureFlag";
import { useApproval } from "#/hooks/useApproval";
import { waitForTransactionReceipt } from "#/services/blockchain/blockchain.service";
import { submitRequest } from "#/services/bridge/bridge.service";
import {
  BungeeToken,
  PermitTypedData,
  RequestStatusEnum,
} from "#/services/bridge/bridge.types";
import { useBridgeStatus } from "#/services/bridge/hooks/useBridgeStatus";
import { useQuote } from "#/services/bridge/hooks/useQuote";
import { useTokensList } from "#/services/bridge/hooks/useTokensList";
import { useBridgeState } from "#/store/app/equityModal/bridgeState";
import { approveToken } from "#/utils/approveToken";
import { fireworks } from "#/utils/fireworks";

export const useBridge = ({
  amount,
  setAmount,
  setInputToken,
  inputToken,
  outputToken,
}: {
  amount: string;
  setAmount: (amount: string) => void;
  setInputToken: (token: BungeeToken) => void;
  inputToken: BungeeToken;
  outputToken: BungeeToken;
}) => {
  const [isBridging, setIsBridging] = useState(false);
  const { primaryWallet } = useDynamicContext();
  const [requestHash, setRequestHash] = useState("");
  const { data: tokens } = useTokensList();
  const { isBridgeDisabled } = useBridgeFeatureFlag();
  const { minAmount: minDepositAmount } = useDepositFeatureFlag();
  const { addBridgeTx } = useBridgeState();

  const parsedAmount = inputToken
    ? parseUnits(amount, inputToken.decimals)
    : 0n;
  const selectedToken = tokens?.find(
    (token) =>
      token.address === inputToken?.address &&
      token.chainId === inputToken?.chainId,
  );
  const balance = BigInt(selectedToken?.balance || "0");
  const isAmountValid = !isNaN(Number(amount)) && Number(amount) > 0;
  const insufficientBalance = parsedAmount > balance;

  const { data: quote, isLoading: isLoadingQuote } = useQuote({
    inputToken,
    amount: parsedAmount,
    outputToken,
    isFormValid: isAmountValid && !insufficientBalance,
  });
  const { data: bridgeStatus } = useBridgeStatus(requestHash);
  const approvalData = quote?.autoRoute?.approvalData;

  const {
    isAllowanceSufficient,
    isLoading: isApprovalLoading,
    refetch: refetchAllowance,
  } = useApproval({
    amount: BigInt(approvalData?.amount || "0"),
    tokenAddress: approvalData?.tokenAddress,
    account: approvalData?.userAddress,
    spender:
      !approvalData || approvalData.spenderAddress === "0"
        ? UNISWAP_PERMIT2
        : approvalData.spenderAddress,
    chainId: inputToken?.chainId,
  });

  const switchNetwork = async (chainId: number) => {
    if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
      return;
    }

    const activeNetwork = await primaryWallet.getNetwork();
    if (Number(activeNetwork) !== chainId) {
      await primaryWallet.switchNetwork(chainId);
    }
  };

  const handleBridge = async () => {
    if (
      !primaryWallet ||
      !quote ||
      !isEthereumWallet(primaryWallet) ||
      !inputToken ||
      isApprovalLoading
    ) {
      return;
    }

    await switchNetwork(inputToken.chainId);

    setRequestHash("");

    const walletClient = (await primaryWallet.getWalletClient(
      inputToken.chainId.toString(),
    )) as WalletClient;

    setIsBridging(true);

    if (approvalData && !isAllowanceSufficient) {
      try {
        const hash = await approveToken({
          amount: BigInt(approvalData.amount),
          spender: approvalData.spenderAddress as Address,
          tokenAddress: approvalData.tokenAddress as Address,
          walletClient,
        });
        await waitForTransactionReceipt(
          {
            hash,
            confirmations: 2,
          },
          inputToken.chainId,
        );
        await refetchAllowance();
        setIsBridging(false);
        return;
      } catch (error) {
        setIsBridging(false);
        return toast.error({
          title: "Error approving token",
        });
      }
    }

    if (!quote.autoRoute) {
      setIsBridging(false);
      return;
    }

    let witness: unknown = null;
    let signTypedData: PermitTypedData | null = null;

    if (quote.autoRoute.signTypedData) {
      signTypedData = quote.autoRoute.signTypedData as PermitTypedData;
      witness = signTypedData?.values?.witness ?? null;
    }

    if (witness && signTypedData) {
      try {
        const quoteId = quote.autoRoute.quoteId;
        const requestType = quote.autoRoute.requestType;

        const signature = await walletClient.signTypedData({
          types: signTypedData.types,
          primaryType: "PermitWitnessTransferFrom",
          message: signTypedData.values,
          domain: signTypedData.domain,
          account: walletClient.account as unknown as Address,
        });

        toast.pending({
          title: "Bridge in progress",
          description: "This may take a few moments.",
        });

        const result = await submitRequest({
          requestType,
          request: witness,
          userSignature: signature,
          quoteId,
        });
        if (result?.requestHash) {
          setRequestHash(result.requestHash);
          addBridgeTx(result.requestHash);
        } else {
          throw new Error("No request hash");
        }
        refetchAllowance();
        return;
      } catch (error) {
        setIsBridging(false);
        return toast.error({
          title: "Error in signing conversion",
        });
      }
    }

    if (quote.autoRoute.txData) {
      try {
        toast.pending({
          title: "Bridge in progress",
          description: "This may take a few moments.",
          duration: 5000,
        });

        const { data, to, value } = quote.autoRoute.txData;

        const hash = await walletClient.sendTransaction({
          account: walletClient.account as unknown as Address,
          data: data as Address,
          to: to as unknown as Address,
          value: value ? BigInt(value) : undefined,
          chain: walletClient.chain,
        });
        if (quote.autoRoute.requestHash) {
          setRequestHash(quote.autoRoute.requestHash);
          addBridgeTx(quote.autoRoute.requestHash);
        } else {
          throw new Error("No request hash");
        }
        await waitForTransactionReceipt(
          {
            hash,
            confirmations: 2,
          },
          inputToken.chainId,
        );
        refetchAllowance();
        return;
      } catch (error) {
        setIsBridging(false);
        return toast.error({
          title: "Error in sending bridge transaction",
        });
      }
    }
  };

  const isBridgeCompleted = bridgeStatus?.bungeeStatusCode
    ? [RequestStatusEnum.FULFILLED, RequestStatusEnum.SETTLED].includes(
        bridgeStatus.bungeeStatusCode,
      )
    : false;

  const isBridgeSettled =
    bridgeStatus?.bungeeStatusCode ?? 0 > RequestStatusEnum.FULFILLED;

  useEffect(() => {
    if (!isBridgeSettled) return;

    setIsBridging(false);
  }, [bridgeStatus?.bungeeStatusCode]);

  useEffect(() => {
    if (!isBridgeCompleted) return;

    setAmount("");
    setInputToken({ ...outputToken });
    const cleanup = fireworks();
    return cleanup;
  }, [isBridgeCompleted, setAmount, setInputToken]);

  const steps = approvalData ? 4 : 3;

  const ctaLabel = evaluate({
    "Bridge is Disabled": isBridgeDisabled,
    "Enter amount": !isAmountValid || insufficientBalance,
    "No quote found": !quote?.autoRoute,
    [`Approve (Step 1/${steps})`]: !isAllowanceSufficient,
    [`Sign Conversion (2/${steps})`]: isAllowanceSufficient,
  });

  const errorMessage = evaluate({
    "Insufficient balance": insufficientBalance && !isBridging,
  });

  const warningMessage = evaluate({
    [`To trade on Kyan, minimum deposit is $${minDepositAmount}`]:
      isAmountValid &&
      quote?.autoRoute &&
      quote.autoRoute.output.valueInUsd < minDepositAmount &&
      !isBridging &&
      !requestHash,
  });

  const isLoading = isBridging || isApprovalLoading || isLoadingQuote;
  const isFormValid =
    isBridgeCompleted || Boolean(isAmountValid && !insufficientBalance);

  return {
    isLoading,
    quote,
    isLoadingQuote,
    handleBridge,
    isDisabled: isBridgeDisabled || !isFormValid,
    ctaLabel,
    errorMessage,
    warningMessage,
    isFormValid,
    bridgeId: requestHash,
    bridgeStatus: bridgeStatus?.bungeeStatusCode,
    max: selectedToken?.balance
      ? formatUnits(BigInt(selectedToken.balance), selectedToken.decimals)
      : "0",
    outputAmount: formatUnits(
      BigInt(quote?.autoRoute?.output?.amount ?? "0"),
      outputToken.decimals,
    ),
  };
};
